import { z } from 'zod';
import { GATEWAY_AUTH_PATH_NAME } from './gateway-auth.constants';
import {
  ExchangeAccessKeyResponseSchema,
  GetAuthConfigResponseSchema,
  UnauthorizedErrorSchema,
  GetByocTenantAttributeResponseSchema,
  GetUserTenantsResponseSchema,
  GetSubTenantsResponseSchema,
  GetAllPlatformTenantsResponseSchema,
  CreateSubTenantRequestSchema,
  CreateSubTenantResponseSchema,
  InviteUserToSubTenantRequestSchema,
  InviteUserResponseSchema,
  ForbiddenErrorSchema,
  ExchangeCodeRequestSchema,
  ExchangeCodeResponseSchema,
  OidcSessionResponseSchema,
  RefreshTokenRequestSchema,
  LocalLoginRequestSchema,
  LocalLoginTokenResponseSchema,
} from './gateway-auth.schemas';
import { PERMISSIONS, terraContract } from '@terra/shared-authorization';

export const GatewayAuthContract = terraContract
  .prefix(`/${GATEWAY_AUTH_PATH_NAME}`)
  .router({
    exchangeAccessKey: terraContract
      .meta({ openApi: true })
      .route({
        method: 'POST',
        path: '/exchange',
        summary: 'Exchange API key for JWT token',
        description: `
Exchange an API access key for a JWT bearer token.

**Usage:**
1. Generate an API key in the Terra dashboard under Settings > API Keys
2. Call this endpoint with the API key in the Authorization header
3. Use the returned JWT token for subsequent API requests

**Request:**
\`\`\`
POST /auth/exchange
Authorization: Bearer <your-api-key>
\`\`\`

**Response:**
\`\`\`json
{
  "jwt": "<jwt-token>"
}
\`\`\`

**Using the JWT:**
Include the JWT in subsequent requests:
\`\`\`
Authorization: Bearer <jwt-token>
\`\`\`

JWT tokens are short-lived and should be refreshed by calling this endpoint again when expired.
      `.trim(),
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          headers: z.object({ authorization: z.string().optional() }),
        }),
      )
      .output(ExchangeAccessKeyResponseSchema)
      .errors({ UNAUTHORIZED: { status: 401, data: UnauthorizedErrorSchema } }),
    getConfig: terraContract
      .meta({ public: true })
      .route({
        method: 'GET',
        path: '/config',
        summary: 'Get public auth configuration',
        description:
          'Returns the public authentication bootstrap configuration for the currently active auth provider. Safe for unauthenticated clients.',
        inputStructure: 'detailed',
      })
      .output(GetAuthConfigResponseSchema),
    exchangeCode: terraContract
      .meta({ public: true })
      .route({
        method: 'POST',
        path: '/oauth/token',
        summary: 'Exchange OAuth code for tokens',
        description: `
Exchange an OAuth authorization code for access tokens via backend proxy.

This endpoint implements the Backend-for-Frontend (BFF) pattern where the
client secret is kept secure on the server. The frontend obtains an
authorization code from the OAuth provider and sends it here for token exchange.

**Flow:**
1. Frontend initiates OAuth flow with PKCE
2. User authenticates with OAuth provider
3. Frontend receives authorization code
4. Frontend calls this endpoint with code and PKCE verifier
5. Backend exchanges code for tokens using client secret
6. Backend returns tokens to frontend

Request body is JSON: { code, code_verifier, redirect_uri }. The gateway sends
form-urlencoded to the IdP per OAuth 2.0; client→gateway uses JSON.
      `.trim(),
        inputStructure: 'detailed',
      })
      .input(z.object({ body: ExchangeCodeRequestSchema }))
      .output(z.union([OidcSessionResponseSchema, ExchangeCodeResponseSchema]))
      .errors({ UNAUTHORIZED: { status: 401, data: UnauthorizedErrorSchema } }),
    refreshToken: terraContract
      .meta({ public: true })
      .route({
        method: 'POST',
        path: '/oauth/refresh',
        summary: 'Refresh OAuth access token',
        description: `
Refresh an OAuth access token using a refresh token via backend proxy.

This endpoint implements the Backend-for-Frontend (BFF) pattern where the
client secret is kept secure on the server. The frontend sends the refresh
token and receives updated access/ID tokens. When using HttpOnly cookie session, call with credentials and no body.
      `.trim(),
        inputStructure: 'detailed',
      })
      .input(z.object({ body: RefreshTokenRequestSchema }))
      .output(z.union([OidcSessionResponseSchema, ExchangeCodeResponseSchema]))
      .errors({ UNAUTHORIZED: { status: 401, data: UnauthorizedErrorSchema } }),
    logout: terraContract
      .meta({ public: true })
      .route({
        method: 'POST',
        path: '/oauth/logout',
        summary: 'Clear OIDC session',
        description:
          'Clears HttpOnly session and refresh cookies. Call with credentials: include before redirecting to IdP logout.',
        inputStructure: 'detailed',
        successStatus: 204,
      })
      .output(z.null())
      .errors({ UNAUTHORIZED: { status: 401, data: UnauthorizedErrorSchema } }),
    getSession: terraContract
      .meta({ public: true })
      .route({
        method: 'GET',
        path: '/oauth/session',
        summary: 'Get current OIDC session',
        description:
          'Returns profile and expires_in when the session cookie is valid. Call with credentials: include to restore session state after page load.',
        inputStructure: 'detailed',
      })
      .output(OidcSessionResponseSchema)
      .errors({ UNAUTHORIZED: { status: 401, data: UnauthorizedErrorSchema } }),
    localLogin: terraContract
      .meta({ public: true })
      .route({
        method: 'POST',
        path: '/login',
        summary: 'Local username/password login',
        description:
          'Authenticates the configured local user and sets an HttpOnly session cookie. Pass x-oidc-response: tokens to receive the JWT in the response body.',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: LocalLoginRequestSchema }))
      .output(
        z.union([OidcSessionResponseSchema, LocalLoginTokenResponseSchema]),
      )
      .errors({ UNAUTHORIZED: { status: 401, data: UnauthorizedErrorSchema } }),
    localGetSession: terraContract
      .meta({ public: true })
      .route({
        method: 'GET',
        path: '/local/session',
        summary: 'Get current local auth session',
        description:
          'Returns profile and expires_in when the local session cookie is valid.',
        inputStructure: 'detailed',
      })
      .output(OidcSessionResponseSchema)
      .errors({ UNAUTHORIZED: { status: 401, data: UnauthorizedErrorSchema } }),
    localLogout: terraContract
      .meta({ public: true })
      .route({
        method: 'POST',
        path: '/local/logout',
        summary: 'Clear local auth session',
        description: 'Clears the local auth HttpOnly session cookie.',
        inputStructure: 'detailed',
        successStatus: 204,
      })
      .output(z.null())
      .errors({ UNAUTHORIZED: { status: 401, data: UnauthorizedErrorSchema } }),
    getTenants: terraContract
      .meta({
        authenticated: true,
      })
      .route({
        method: 'GET',
        path: '/tenants',
        summary: 'Get user tenants',
        description:
          'Get all tenants the current user has access to, including roles and permissions.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: z.object({
            fresh: z
              .enum(['true', 'false'])
              .optional()
              .describe(
                'When "true", bypasses cache and fetches fresh tenant data from the identity provider.',
              ),
            torchSupportedOnly: z
              .enum(['true', 'false'])
              .optional()
              .describe(
                'When "true", returns only tenants whose license includes Torch support.',
              ),
          }),
        }),
      )
      .output(GetUserTenantsResponseSchema)
      .errors({ UNAUTHORIZED: { status: 401, data: UnauthorizedErrorSchema } }),
    getByocTenantAttribute: terraContract
      .meta({
        authenticated: true,
      })
      .route({
        method: 'GET',
        path: '/byoc-tenant',
        summary: 'Get current tenant bring-your-own-cloud attribute',
        description:
          'Loads the current tenant custom attribute that marks bring-your-own-cloud deployment.',
        inputStructure: 'detailed',
      })
      .output(GetByocTenantAttributeResponseSchema)
      .errors({ UNAUTHORIZED: { status: 401, data: UnauthorizedErrorSchema } }),
    getAllPlatformTenants: terraContract
      .meta({
        authenticated: true,
      })
      .route({
        method: 'GET',
        path: '/tenants/all',
        summary: 'Get all platform tenants',
        description:
          'Get all tenants and sub-tenants in the entire platform. Restricted to Terra users only.',
        inputStructure: 'detailed',
      })
      .output(GetAllPlatformTenantsResponseSchema)
      .errors({
        UNAUTHORIZED: { status: 401, data: UnauthorizedErrorSchema },
        FORBIDDEN: { status: 403, data: ForbiddenErrorSchema },
      }),
    getSubTenants: terraContract
      .meta({
        permissions: [PERMISSIONS.USER_ADMIN.name],
      })
      .route({
        method: 'GET',
        path: '/sub-tenants',
        summary: 'Get sub-tenants',
        description: 'Get all sub-tenants of the current tenant.',
        inputStructure: 'detailed',
      })
      .output(GetSubTenantsResponseSchema)
      .errors({
        UNAUTHORIZED: { status: 401, data: UnauthorizedErrorSchema },
        FORBIDDEN: { status: 403, data: ForbiddenErrorSchema },
      }),
    createSubTenant: terraContract
      .meta({
        permissions: [PERMISSIONS.USER_ADMIN.name],
      })
      .route({
        method: 'POST',
        path: '/sub-tenants',
        summary: 'Create sub-tenant',
        description: 'Create a new sub-tenant under the current tenant.',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(z.object({ body: CreateSubTenantRequestSchema }))
      .output(CreateSubTenantResponseSchema)
      .errors({
        UNAUTHORIZED: { status: 401, data: UnauthorizedErrorSchema },
        FORBIDDEN: { status: 403, data: ForbiddenErrorSchema },
      }),
    deleteSubTenant: terraContract
      .meta({
        permissions: [PERMISSIONS.USER_ADMIN.name],
      })
      .route({
        method: 'DELETE',
        path: '/sub-tenants/{subTenantId}',
        summary: 'Delete sub-tenant',
        description: 'Delete a sub-tenant and all its data.',
        inputStructure: 'detailed',
        successStatus: 204,
      })
      .input(z.object({ params: z.object({ subTenantId: z.string() }) }))
      .output(z.null())
      .errors({
        UNAUTHORIZED: { status: 401, data: UnauthorizedErrorSchema },
        FORBIDDEN: { status: 403, data: ForbiddenErrorSchema },
      }),
    inviteUserToSubTenant: terraContract
      .meta({
        permissions: [PERMISSIONS.USER_ADMIN.name],
      })
      .route({
        method: 'POST',
        path: '/sub-tenants/{subTenantId}/invite',
        summary: 'Invite user to sub-tenant',
        description: 'Invite a user to join a sub-tenant with specified roles.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ subTenantId: z.string() }),
          body: InviteUserToSubTenantRequestSchema,
        }),
      )
      .output(InviteUserResponseSchema)
      .errors({
        UNAUTHORIZED: { status: 401, data: UnauthorizedErrorSchema },
        FORBIDDEN: { status: 403, data: ForbiddenErrorSchema },
      }),
  });
